import * as React from "react";
import { Toast, ToastTitle, ToastBody, ToastTrigger, Spinner, Button } from "@fluentui/react-components";
import { Dismiss20Regular } from "@fluentui/react-icons";
import { FormattedMessage } from "react-intl";

const dismissButton = <Button icon={<Dismiss20Regular />} size="small" appearance="transparent" />;

export class MediaProgressManager {
  constructor(toastEmitter) {
    this._toastEmitter = toastEmitter;
    this._trackedNonFancyElements = new Map();
    this._globalProgressToastId = null;
    this._totalRegisteredInBatch = 0;
    this._completedInBatch = 0;
    this._skippedInBatch = 0;
    this._currentBatchManuallyDismissed = false;

    if (this._toastEmitter && typeof this._toastEmitter.on === "function") {
      this._toastEmitter.on("toastDismissedByUser", toastId => {
        if (toastId === this._globalProgressToastId) {
          this._currentBatchManuallyDismissed = true;
          this._globalProgressToastId = null;
        }
      });
    }
  }

  registerNonFancyLoad(elementId, elementName = "item") {
    if (!this._trackedNonFancyElements.has(elementId)) {
      if (this._trackedNonFancyElements.size === 0) {
        this._totalRegisteredInBatch = 0;
        this._completedInBatch = 0;
        this._skippedInBatch = 0;
        this._currentBatchManuallyDismissed = false;
      }
      this._trackedNonFancyElements.set(elementId, { elementName, isSkipped: false });
      this._totalRegisteredInBatch++;
      this._updateGlobalProgressToast();
    }
  }

  reportNonFancyLoadOutcome(elementId, wasSkipped = false, isRemoval = false) {
    if (this._trackedNonFancyElements.has(elementId)) {
      const item = this._trackedNonFancyElements.get(elementId);
      if (wasSkipped) {
        item.isSkipped = true;
        this._skippedInBatch++;
      }
      this._completedInBatch++;
      this._trackedNonFancyElements.delete(elementId);
      this._updateGlobalProgressToast();
    } else if (isRemoval && this._totalRegisteredInBatch > this._completedInBatch) {
      this._completedInBatch++;
      this._updateGlobalProgressToast();
    }
  }

  _updateGlobalProgressToast() {
    if (this._currentBatchManuallyDismissed && this._globalProgressToastId === null) {
      if (this._trackedNonFancyElements.size === 0 && this._completedInBatch >= this._totalRegisteredInBatch) {
        this._currentBatchManuallyDismissed = false;
      }
      return;
    }
    const itemsStillPending = this._totalRegisteredInBatch - this._completedInBatch;
    if (itemsStillPending > 0 || (this._trackedNonFancyElements.size > 0 && this._totalRegisteredInBatch > 0)) {
      const message = `${this._completedInBatch} of ${this._totalRegisteredInBatch} loaded`;
      // if (this._skippedInBatch > 0) {
      //   message += ` (${this._skippedInBatch} skipped)`;
      // }
      const newToastInstanceId = "global-media-progress";
      const toastContent = (
        <Toast>
          <ToastTitle
            media={<Spinner size="tiny" />}
            action={
              <ToastTrigger
                onClick={() => {
                  this._currentBatchManuallyDismissed = true;
                  this._toastEmitter.emit("dismissToast", newToastInstanceId);
                }}
              >
                {dismissButton}
              </ToastTrigger>
            }
          >
            <FormattedMessage id="toast.loading-objects" defaultMessage="Loading Objects" />
          </ToastTitle>
          <ToastBody>{message}</ToastBody>
        </Toast>
      );
      const options = {
        toastId: newToastInstanceId,
        intent: "info",
        timeout: -1
      };
      if (this._globalProgressToastId) {
        this._toastEmitter.emit("updateToast", toastContent, options);
      } else {
        this._toastEmitter.emit("toast", toastContent, options);
        this._globalProgressToastId = newToastInstanceId;
      }
    } else if (this._globalProgressToastId) {
      this._toastEmitter.emit("dismissToast", this._globalProgressToastId);
      this._globalProgressToastId = null;
      this._totalRegisteredInBatch = 0;
      this._completedInBatch = 0;
      this._skippedInBatch = 0;
      this._currentBatchManuallyDismissed = false;
    }
  }

  resetProgress() {
    if (this._globalProgressToastId) {
      this._toastEmitter.emit("dismissToast", this._globalProgressToastId);
    }
    this._trackedNonFancyElements.clear();
    this._globalProgressToastId = null;
    this._totalRegisteredInBatch = 0;
    this._completedInBatch = 0;
    this._skippedInBatch = 0;
    this._currentBatchManuallyDismissed = false;
  }
}
